import { FunctionComponent, useReducer } from "react";
import ListItem from "../ListItem/ListItem";
import Column from "../Column/Column";
import OptInSummary, { OptInSummaryProps } from "./OptInSummary";
import CheckBox, { CheckBoxProps } from "../CheckBox/CheckBox";
import { Heading } from "../Text/Heading";
import Button from "../Button/Button";
import Link from "next/link";

export interface OptInData {
  id: string;
  title: string;
  heading: string;
  description: string;
  type: string;
  validations: {
    required?: boolean;
  };
  components: Array<any>;
  properties: {
    cta: {
      text: string;
      button: string;
    };
  };
}

type OptInProps = {
  data: OptInData;
  onDataSelected: Function;
  onBack?: Function;
  errors: { [key: string]: String };
  answers: any;
  score: { [key: string]: any };
  isLoading: boolean;
};

const OptIn: FunctionComponent<OptInProps> = ({
  data,
  onDataSelected,
  onBack,
  answers,
  errors,
  score,
  isLoading,
}) => {
  let initialState: { [key: string]: any } = {};
  data.components.forEach((el) => {
    if (el.componentType === "input") {
      const id: any = el.id;
      initialState[id] = "";
    }
  });
  const [formInput, setFormInput] = useReducer(
    (state: any, newState: any) => ({ ...state, ...newState }),
    initialState
  );
  const onInputChange = (value: string | boolean, id: string) => {
    if (value === true) {
      setFormInput({ [id]: "accepted" });
    } else if (value === false) {
      setFormInput({ [id]: false });
    } else {
      setFormInput({ [id]: value });
    }
  };
  const formattedInput = (input: { [key: string]: any }) => {
    const inputArray = Object.keys(input).map((id) => {
      const label = data.components.find((el) => el.id === id)?.label;
      return { id, value: input[id], label };
    });
    return inputArray;
  };
  const checkboxes = data.components.map((el) => {
    if (el.componentType === "input") {
      return (
        <div className="mb-8" key={el.id}>
          <CheckBox
            {...el}
            setChecked={onInputChange}
            error={!!errors[el.id]}
            errorText={errors[el.id]}
          />
        </div>
      );
    }
    return null;
  });
  const summary = data.components.find((el) => el.componentType === "summary");
  const linksArray: any[] = JSON.parse(JSON.stringify(summary.links)) || [];
  const links = linksArray.map((el: any, index: number) => {
    return (
      // <a target="_blank" href={el.link}>
      // </a>
      <Button
        key={index}
        elementType="a"
        target="_blank"
        href={el.link}
        variant="outline"
        size="small"
        className={`w-1/2 text-center ${index > 0 ? "ml-2" : ""}`}
      >
        {el.label}
      </Button>
    );
  });
  const onSelectData = (customAnswer?: { [key: string]: any }) => {
    let answer: { [key: string]: any } = {};

    if (customAnswer) {
      answer[customAnswer.id] = customAnswer;
    } else {
      formattedInput(formInput).forEach((el) => {
        answer[el.id] = el;
      });
    }
    const selectedData = {
      type: "opt-in",
      page: { type: "opt-in", id: data.id },
      data: answer,
    };
    onDataSelected(selectedData);
  };

  return (
    <div className="w-full max-w-4xl">
      <div className="flex flex-wrap items-stretch mb-12 lg:-mx-8">
        <div className="px-2 py-2 w-full lg:py-0 lg:px-8 lg:w-1/2">
          <OptInSummary {...summary} answers={answers} score={score} />
        </div>
        <div className="px-2 py-2 w-full lg:py-0 lg:px-8 lg:w-1/2">
          <Heading level={4} className="mb-4">
            Letzter Schritt
          </Heading>
          <div className="flex w-full -px-4 mb-8">{links}</div>
          <div className="mb-16">{checkboxes}</div>
          <p className="font-medium mb-4">
            Schließe deinen Mitgliedschaftsvertrag mit einem Klick auf den
            untenstehenden Button verbindlich ab.
          </p>
          <Button
            variant="primary"
            className="w-full mb-2"
            id={`${data.id}–submit-button`}
            isLoading={isLoading}
            onPress={() => onSelectData()}
          >
            Mitgliedschaft Abschließen
          </Button>
          <p className="text-center text-sm text-gray-500">
            Deine Daten sind zu 100% sicher und werden ausschließlich
            verschlüsselt versendet.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OptIn;
